import React, { useEffect, useState } from 'react';
import { UserPlus, Trash2, ShieldCheck, User as UserIcon } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const UsersManager = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: '', password: '', role: 'editor' });

  const load = () => api.get('/users').then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', form);
      toast({ title: 'User created', description: `${form.username} (${form.role})` });
      setForm({ username: '', password: '', role: 'editor' });
      load();
    } catch (err) {
      toast({ title: 'Could not create user', description: err?.response?.data?.detail || 'Try a different username.', variant: 'destructive' });
    }
  };

  const remove = async (id, uname) => {
    if (!window.confirm(`Delete user "${uname}"?`)) return;
    try { await api.delete(`/users/${id}`); load(); toast({ title: 'User deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-sand p-6 mb-8">
        <h3 className="font-display text-xl text-forest mb-4">Add a team member</h3>
        <p className="text-sm text-ink/60 mb-4">Editors can sign in and upload photos & videos. Owners can manage everything.</p>
        <form onSubmit={create} className="grid sm:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-600 text-ink/60 mb-1">Username</label>
            <input required minLength={3} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" placeholder="jane" />
          </div>
          <div>
            <label className="block text-xs font-600 text-ink/60 mb-1">Password</label>
            <input required minLength={4} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" placeholder="set a password" />
          </div>
          <div>
            <label className="block text-xs font-600 text-ink/60 mb-1">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none"><option value="editor">Editor</option><option value="owner">Owner</option></select>
          </div>
          <button type="submit" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light"><UserPlus className="w-4 h-4" /> Add</button>
        </form>
      </div>

      <h3 className="font-display text-xl text-forest mb-4">Team ({users.length})</h3>
      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-4 bg-white rounded-xl border border-sand p-4">
            <span className={`w-10 h-10 rounded-full flex items-center justify-center ${u.role === 'owner' ? 'bg-forest text-gold-light' : 'bg-sand text-forest'}`}>{u.role === 'owner' ? <ShieldCheck className="w-5 h-5" /> : <UserIcon className="w-5 h-5" />}</span>
            <div className="flex-1">
              <p className="font-600 text-forest">{u.username} {u.username === user?.username && <span className="text-xs text-ink/40">(you)</span>}</p>
              <p className="text-xs text-ink/55 capitalize">{u.role}</p>
            </div>
            {u.username !== user?.username && (
              <button onClick={() => remove(u.id, u.username)} className="w-9 h-9 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsersManager;
